import * as React from "react";
import {
  Button,
  Checkbox,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  makeStyles,
  Theme,
  createStyles,
  Tooltip,
} from "@material-ui/core";
import { isEqual } from "lodash";
import { useSnackbar } from "notistack";
import EUser from "../../../core/contracts/EUser";
import { useHubXValue } from "../../../core/hooks/useHubHooks";
import {
  EVENT_ADD_NEW_MEMBERS,
  EVENT_SEARCH_FIELD_CHANGE,
  EVENT_SEARCH_FIELD_EXTERNAL_CHANGE,
} from "../../../Events";
import { FILTER_USERS } from "../../../Fedx";
import { useHubx } from "../../../HubX";
import SearchField from "../../SmartFilters/SearchField";
import { isArrayValid } from "../../../helpers/utility";
import { Skeleton } from "@material-ui/lab";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(3, 1),
    },

    addButton: {
      color: theme.palette.common.white,
    },

    tableWrapper: {
      "margin": theme.spacing(2, 0),

      "& .MuiTableCell-stickyHeader": {
        backgroundColor: "#1D212A",
        color: theme.palette.common.white,
      },
    },
  })
);

type AddMemberDialogProps = {
  handleClickCancel: () => void;
};

const ADD_MEMBER_SEARCH_FIELD_ID = "add.member.search.field.id";

function AddMemberDialog({ handleClickCancel }: AddMemberDialogProps) {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [users, setUsers] = React.useState<EUser[]>([]);
  const [selectedUsers, setSelectedUsers] = React.useState<string[]>([]);

  const hub = useHubx();
  const toastr = useSnackbar();
  const classes = useStyles();

  const searchEmail = useHubXValue<string>(
    EVENT_SEARCH_FIELD_CHANGE(ADD_MEMBER_SEARCH_FIELD_ID),
    ""
  );

  const allUsersCodes = React.useMemo(() => {
    return users.map((x) => x.codigo);
  }, [users]);

  const filterUsers = async () => {
    setLoading(true);
    try {
      const APIResponse: EUser[] = await hub.request(FILTER_USERS, searchEmail);
      setUsers(APIResponse);
    } catch (error) {
      // eslint-disable-next-line no-console
      toastr.enqueueSnackbar("Ops! Não foi possível recuperar os membros desta pesquisa!", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (userCode: string) => {
    if (selectedUsers.includes(userCode)) {
      setSelectedUsers((prev) => prev.filter((x) => x !== userCode));
    } else {
      setSelectedUsers((prev) => [...prev, userCode]);
    }
  };

  const handleClickSelectAll = () => {
    if (isEqual(allUsersCodes, selectedUsers)) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(allUsersCodes);
    }
  };

  React.useEffect(() => {
    if (searchEmail.length >= 3 && !loading) {
      filterUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchEmail]);

  const doCancel = () => {
    setSelectedUsers([]);
    setUsers([]);
    hub.notify(EVENT_SEARCH_FIELD_EXTERNAL_CHANGE(ADD_MEMBER_SEARCH_FIELD_ID), "");
    handleClickCancel();
  };

  const handleCLickAdd = () => {
    const selectedUsersDTO = users.filter((user) => selectedUsers.includes(user.codigo));

    hub.notify(EVENT_ADD_NEW_MEMBERS, selectedUsersDTO);
    doCancel();
  };

  const isOnError = !isArrayValid(selectedUsers);

  const loadingState = new Array(6).fill({});

  const renderValue = (value?: string | number | null) => {
    return value || value === 0 ? value : "-";
  };

  return (
    <div className={classes.root}>
      <div>
        <SearchField
          eventId={ADD_MEMBER_SEARCH_FIELD_ID}
          label={"Nome ou email"}
          helperText={
            searchEmail.length < 3 ? "Para pesquisar, por favor, insira ao menos 3 caracteres" : ""
          }
        />
      </div>

      <div className={classes.tableWrapper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={isArrayValid(allUsersCodes) && isEqual(allUsersCodes, selectedUsers)}
                  onClick={handleClickSelectAll}
                />
              </TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Nome de Usuário</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!loading &&
              users?.map((user) => (
                <TableRow key={user.codigo}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedUsers.includes(user.codigo)}
                      onClick={() => handleClick(user.codigo)}
                    />
                  </TableCell>
                  <TableCell>{renderValue(user?.email)}</TableCell>
                  <TableCell>{renderValue(user?.nome)}</TableCell>
                </TableRow>
              ))}

            {loading &&
              loadingState.map((_, idx) => (
                <TableRow key={`loading_row_${idx}`}>
                  <TableCell colSpan={3}>
                    <Skeleton />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      <DialogActions>
        <Button variant="outlined" onClick={doCancel}>
          Cancelar
        </Button>

        <Tooltip title={isOnError ? "Por favor, selecione algum usuário para adicionar" : ""}>
          <span>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCLickAdd}
              className={classes.addButton}
              disabled={isOnError}
            >
              Adicionar
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </div>
  );
}

export default AddMemberDialog;
